import {
  MAX_CUSTOM_DOMAINS_PER_STORE,
  canIssueCertificate,
  type AddDomainRequest,
  type DnsSetupOption,
  type Domain,
  type DomainDetail,
  type DomainObservation,
  type DomainStatus,
  type IssueCertificateResponse,
} from '@avrahami/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cell,
  ConfirmDialog,
  fill,
  formatDate,
  formatDateTime,
  isApiError,
  Loading,
  PageHeader,
  returnToPrevious,
  Table,
  TextField,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import {
  keys,
  platformApi,
  tenantDomainQuery,
  tenantDomainsQuery,
  tenantQuery,
} from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { DomainStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import {
  addressText,
  addDomainRequest,
  canCheckNow,
  canMakePrimary,
  canRemove,
  detailBehind,
  dnsZone,
  domainsFull,
  isRootAddress,
  missingTwin,
  needsPolling,
  needsSetup,
  openDomain,
  reasonText,
  recordHost,
  setupKinds,
  setupOption,
  showsReason,
  sortDomains,
  statusText,
} from '../lib/domains';
import { serverFieldErrors } from '../lib/forms';
import { errorMessage } from '../lib/format';

/**
 * One store's addresses: its system address, and the domains of its own the platform staff
 * connect to it (adr/0025). The store's own admin has no such screen.
 */
export function TenantDomainsPage() {
  const { tenantId } = useParams({ from: '/app/tenants/$tenantId/domains' });
  const session = useQuery(sessionQuery);
  const can = (capability: 'platform.tenants.read' | 'platform.tenants.create') =>
    session.data !== undefined && hasCapability(session.data, capability);
  const canRead = can('platform.tenants.read');
  const canManage = can('platform.tenants.create');
  // Only for the store's name in the title: the screen works without it.
  const tenant = useQuery({ ...tenantQuery(tenantId), enabled: canRead });
  const list = useQuery({ ...tenantDomainsQuery(tenantId), enabled: canRead });
  const name = tenant.data?.tenant.displayName;
  return (
    <>
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId }}
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.storeDomains.back}
      </Link>
      <PageHeader
        title={name === undefined ? he.storeDomains.title : `${he.storeDomains.title}: ${name}`}
      />
      {canRead ? (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-slate-600">{he.storeDomains.intro}</p>
          {canManage ? null : <Alert tone="info">{he.storeDomains.readOnly}</Alert>}
          {list.isPending ? <Loading /> : null}
          {list.isError ? (
            <LoadError
              error={list.error}
              busy={list.isFetching}
              onRetry={() => {
                void list.refetch();
              }}
            />
          ) : null}
          {list.data === undefined ? null : (
            <Domains
              key={tenantId}
              tenantId={tenantId}
              domains={sortDomains(list.data.items)}
              canManage={canManage}
              certificateIssuing={list.data.certificateIssuing}
            />
          )}
        </div>
      ) : (
        <Alert>{he.storeDomains.forbidden}</Alert>
      )}
    </>
  );
}

/** A list or an address that could not be read, with a way to ask again. */
function LoadError({
  error,
  busy,
  onRetry,
}: {
  error: unknown;
  busy: boolean;
  onRetry: () => void;
}) {
  return (
    <Alert>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>
          {errorMessage(error, {
            FORBIDDEN: he.storeDomains.forbidden,
            NOT_FOUND: he.storeDomains.notFound,
          })}
        </span>
        <Button tone="secondary" busy={busy} onClick={onRetry}>
          {he.common.retry}
        </Button>
      </div>
    </Alert>
  );
}

/** Every status of the monitoring list (`keys.domains`). */
const MONITORING_KEY = ['domains'] as const;

function Domains({
  tenantId,
  domains,
  canManage,
  certificateIssuing,
}: {
  tenantId: string;
  domains: readonly Domain[];
  canManage: boolean;
  /** The staff issue an own domain's certificate with a button here (adr/0029). */
  certificateIssuing: boolean;
}) {
  const queryClient = useQueryClient();
  const [chosenId, setChosenId] = useState<string>();
  // The address just added: its card takes the focus, so the next step is read first.
  const [addedId, setAddedId] = useState<string>();
  const [removing, setRemoving] = useState<Domain>();
  const listRef = useRef<HTMLDivElement>(null);

  // The store's page and the monitoring list show these addresses too.
  const refreshOthers = () =>
    Promise.all(
      [keys.tenant(tenantId), MONITORING_KEY].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.tenantDomainList(tenantId) }),
      refreshOthers(),
    ]);
  };

  // The checks ended: an address that became active may have become the primary one.
  const working = needsPolling(domains);
  const wasWorking = useRef(working);
  useEffect(() => {
    const ended = wasWorking.current && !working;
    wasWorking.current = working;
    if (!ended) return;
    for (const queryKey of [keys.tenantDomains(tenantId), keys.tenant(tenantId), MONITORING_KEY]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [working, queryClient, tenantId]);

  const check = useMutation({
    mutationFn: (domainId: string) => platformApi.checkDomain(tenantId, domainId),
    onSuccess: async (detail) => {
      queryClient.setQueryData(tenantDomainQuery(tenantId, detail.domain.id).queryKey, detail);
      setChosenId(detail.domain.id);
      await refresh();
    },
    // The address changed or was removed elsewhere: show the list as it is now.
    onError: refresh,
  });
  const primary = useMutation({
    mutationFn: (domainId: string) => platformApi.makeDomainPrimary(tenantId, domainId),
    onSuccess: async (result) => {
      queryClient.setQueryData(tenantDomainsQuery(tenantId).queryKey, result);
      await refreshOthers();
    },
    // The address stopped being active meanwhile: show the list as it is now.
    onError: refresh,
  });
  const remove = useMutation({
    mutationFn: (domain: Domain) => platformApi.removeDomain(tenantId, domain.id),
    onSuccess: async (_nothing, domain) => {
      setRemoving(undefined);
      await refresh();
      queryClient.removeQueries({ queryKey: keys.tenantDomain(tenantId, domain.id) });
      // The row and its buttons are gone: the focus goes back to the list.
      listRef.current?.focus();
    },
  });
  // The server makes the site and obtains the certificate while this waits.
  const issue = useMutation({
    mutationFn: (domainId: string) => platformApi.issueDomainCertificate(tenantId, domainId),
    onSuccess: async (outcome) => {
      const { domain } = outcome.detail;
      queryClient.setQueryData(tenantDomainQuery(tenantId, domain.id).queryKey, outcome.detail);
      await refresh();
    },
    // The address changed or was removed elsewhere: show the list as it is now.
    onError: refresh,
  });
  const acting = check.isPending || primary.isPending || remove.isPending || issue.isPending;

  const startCheck = (domain: Domain) => {
    primary.reset();
    issue.reset();
    check.mutate(domain.id);
  };
  const mayIssue = (domain: Domain) =>
    canManage && certificateIssuing && canIssueCertificate(domain);
  const startIssue = (domain: Domain) => {
    check.reset();
    primary.reset();
    // Its card says what is happening, and then how it ended.
    setChosenId(domain.id);
    issue.mutate(domain.id);
  };
  const shown = openDomain(domains, chosenId);

  return (
    <>
      <div
        ref={listRef}
        tabIndex={-1}
        className="rounded-lg focus:outline-2 focus:outline-brand-600"
      >
        <Card title={he.storeDomains.listTitle}>
          {domains.length === 0 ? (
            <p className="text-sm text-slate-500">{he.storeDomains.empty}</p>
          ) : (
            <Table
              head={[
                he.storeDomains.hostname,
                he.storeDomains.statusHead,
                he.storeDomains.lastChecked,
                he.storeDomains.actions,
              ]}
            >
              {domains.map((domain) => {
                const failed =
                  check.isError && check.variables === domain.id
                    ? check.error
                    : primary.isError && primary.variables === domain.id
                      ? primary.error
                      : issue.isError && issue.variables === domain.id
                        ? issue.error
                        : undefined;
                return (
                  <tr
                    key={domain.id}
                    data-testid={`domain-row-${domain.hostname}`}
                    data-kind={domain.kind}
                    data-primary={String(domain.isPrimary)}
                  >
                    <Cell>
                      <div className="flex flex-wrap items-center gap-2">
                        <a
                          href={domain.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          dir="ltr"
                          title={he.storeDomains.opensInNewTab}
                          className="ltr font-medium text-brand-700 underline"
                        >
                          {addressText(domain.url)}
                        </a>
                        {domain.isPrimary ? (
                          <span data-testid="domain-primary">
                            <Badge tone="blue">{he.storeDomains.primary}</Badge>
                          </span>
                        ) : null}
                      </div>
                    </Cell>
                    <Cell>
                      <DomainStatusBadge domain={domain} />
                    </Cell>
                    <Cell className="whitespace-nowrap text-xs text-slate-600">
                      {domain.kind === 'SYSTEM'
                        ? he.common.none
                        : domain.lastCheckedAt === null
                          ? he.storeDomains.neverChecked
                          : formatDateTime(domain.lastCheckedAt)}
                    </Cell>
                    <Cell>
                      <div className="flex gap-2 whitespace-nowrap">
                        {canManage && canMakePrimary(domain) ? (
                          <Button
                            tone="secondary"
                            data-testid="domain-primary-button"
                            aria-label={actionName(he.storeDomains.makePrimary, domain)}
                            busy={primary.isPending && primary.variables === domain.id}
                            disabled={acting}
                            onClick={() => {
                              check.reset();
                              issue.reset();
                              primary.mutate(domain.id);
                            }}
                          >
                            {he.storeDomains.makePrimary}
                          </Button>
                        ) : null}
                        {mayIssue(domain) ? (
                          <Button
                            data-testid="domain-issue"
                            aria-label={actionName(he.storeDomains.issueCertificate, domain)}
                            busy={issue.isPending && issue.variables === domain.id}
                            disabled={acting}
                            onClick={() => {
                              startIssue(domain);
                            }}
                          >
                            {he.storeDomains.issueCertificate}
                          </Button>
                        ) : null}
                        {canManage && canCheckNow(domain) ? (
                          <Button
                            tone="secondary"
                            data-testid="domain-check"
                            aria-label={actionName(he.storeDomains.checkNow, domain)}
                            busy={check.isPending && check.variables === domain.id}
                            disabled={acting}
                            onClick={() => {
                              startCheck(domain);
                            }}
                          >
                            {he.storeDomains.checkNow}
                          </Button>
                        ) : null}
                        {domain.kind === 'CUSTOM' ? (
                          <Button
                            tone="secondary"
                            data-testid="domain-details"
                            aria-label={actionName(he.storeDomains.details, domain)}
                            aria-pressed={shown?.id === domain.id}
                            onClick={() => {
                              setChosenId(domain.id);
                            }}
                          >
                            {he.storeDomains.details}
                          </Button>
                        ) : null}
                        {canManage && canRemove(domain) ? (
                          <Button
                            tone="secondary"
                            data-testid="domain-remove"
                            aria-label={actionName(he.storeDomains.remove, domain)}
                            disabled={acting}
                            onClick={() => {
                              remove.reset();
                              setRemoving(domain);
                            }}
                          >
                            {he.storeDomains.remove}
                          </Button>
                        ) : null}
                      </div>
                      {failed === undefined ? null : (
                        <p role="alert" className="mt-2 text-xs text-red-600">
                          {errorMessage(failed)}
                        </p>
                      )}
                    </Cell>
                  </tr>
                );
              })}
            </Table>
          )}
        </Card>
      </div>

      {canManage ? (
        <AddDomain
          tenantId={tenantId}
          domains={domains}
          onAdded={async (detail) => {
            queryClient.setQueryData(
              tenantDomainQuery(tenantId, detail.domain.id).queryKey,
              detail,
            );
            setChosenId(detail.domain.id);
            setAddedId(detail.domain.id);
            await refresh();
          }}
          onConflict={refresh}
        />
      ) : null}

      {shown === undefined ? null : (
        <DomainCard
          key={shown.id}
          tenantId={tenantId}
          listed={shown}
          domains={domains}
          takeFocus={addedId === shown.id}
          certificateIssuing={certificateIssuing}
          issuing={issue.isPending && issue.variables === shown.id}
          issued={issue.isSuccess && issue.variables === shown.id ? issue.data : undefined}
          issue={
            mayIssue(shown)
              ? {
                  disabled: acting,
                  run: () => {
                    startIssue(shown);
                  },
                }
              : undefined
          }
          retry={
            canManage && canCheckNow(shown)
              ? {
                  busy: check.isPending && check.variables === shown.id,
                  disabled: acting,
                  run: () => {
                    startCheck(shown);
                  },
                }
              : undefined
          }
        />
      )}

      <ConfirmDialog
        open={removing !== undefined}
        title={he.storeDomains.removeTitle}
        confirmLabel={he.storeDomains.remove}
        confirmTestId="domain-confirm-remove"
        busy={remove.isPending}
        onConfirm={() => {
          if (removing !== undefined) remove.mutate(removing);
        }}
        onClose={() => {
          setRemoving(undefined);
        }}
      >
        {removing === undefined ? null : (
          <p>{fill(he.storeDomains.removeBody, { hostname: removing.hostname })}</p>
        )}
        {remove.isError ? (
          <div className="mt-3">
            <Alert>{errorMessage(remove.error)}</Alert>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

/** A row button's name for screen readers: several rows have the same buttons. */
function actionName(action: string, domain: Domain): string {
  return fill(he.storeDomains.actionFor, { action, hostname: domain.hostname });
}

function AddDomain({
  tenantId,
  domains,
  onAdded,
  onConflict,
}: {
  tenantId: string;
  domains: readonly Domain[];
  onAdded: (detail: DomainDetail) => Promise<void>;
  onConflict: () => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [localError, setLocalError] = useState<string>();
  const full = domainsFull(domains);
  const add = useMutation({
    mutationFn: (body: AddDomainRequest) => platformApi.addDomain(tenantId, body),
    onSuccess: async (detail) => {
      setText('');
      await onAdded(detail);
    },
    // Taken, or the store reached its limit in another tab: show the list as it is now.
    onError: async (error) => {
      if (isApiError(error, 'CONFLICT')) await onConflict();
    },
  });

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = addDomainRequest(text);
    if (!parsed.ok) {
      add.reset();
      setLocalError(parsed.errors['hostname'] ?? he.common.invalidField);
      return;
    }
    setLocalError(undefined);
    // The field shows the address as it is sent.
    setText(parsed.value.hostname);
    add.mutate(parsed.value);
  };

  const rejected = isApiError(add.error, 'VALIDATION_FAILED')
    ? (serverFieldErrors(add.error)['hostname'] ?? he.common.invalidField)
    : undefined;
  // At the limit the note below already says why the address was not added.
  const failed =
    add.isError && rejected === undefined && !(full && isApiError(add.error, 'CONFLICT'));

  return (
    <Card title={he.storeDomains.addTitle}>
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="domain-form"
      >
        <TextField
          label={he.storeDomains.addLabel}
          name="hostname"
          dir="ltr"
          inputMode="url"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={300}
          placeholder={he.storeDomains.addPlaceholder}
          hint={he.storeDomains.addHint}
          className="max-w-md"
          data-testid="domain-hostname-input"
          value={text}
          error={localError ?? rejected}
          disabled={full || add.isPending}
          onChange={(event) => {
            setText(event.target.value);
            setLocalError(undefined);
            if (add.isError) add.reset();
          }}
        />
        {full ? (
          <div data-testid="domain-full">
            <Alert tone="info">
              {fill(he.storeDomains.full, { max: MAX_CUSTOM_DOMAINS_PER_STORE })}
            </Alert>
          </div>
        ) : null}
        {failed ? (
          <div data-testid="domain-add-error">
            <Alert>{errorMessage(add.error, { CONFLICT: he.storeDomains.taken })}</Alert>
          </div>
        ) : null}
        <div>
          <Button
            type="submit"
            busy={add.isPending}
            disabled={full}
            data-testid="domain-add-submit"
          >
            {he.storeDomains.add}
          </Button>
        </div>
      </form>
    </Card>
  );
}

const statusTones = {
  PENDING: 'info',
  VERIFYING: 'info',
  ACTIVE: 'success',
  FAILED: 'error',
  DISABLED: 'warning',
  DNS_LOST: 'warning',
} as const satisfies Record<DomainStatus, 'error' | 'info' | 'success' | 'warning'>;

/** One own address: where it stands, the DNS records it needs, and what the last check saw. */
function DomainCard({
  tenantId,
  listed,
  domains,
  takeFocus,
  certificateIssuing,
  issuing,
  issued,
  issue,
  retry,
}: {
  tenantId: string;
  /** The address as the list has it. */
  listed: Domain;
  domains: readonly Domain[];
  takeFocus: boolean;
  certificateIssuing: boolean;
  /** The server is working on this address's certificate right now. */
  issuing: boolean;
  /** How the last request for its certificate ended, while the screen is open. */
  issued: IssueCertificateResponse | undefined;
  /** "Issue certificate", when the address is ready for it and the role may ask. */
  issue: { disabled: boolean; run: () => void } | undefined;
  /** "Check now", for a role that may ask for it. */
  retry: { busy: boolean; disabled: boolean; run: () => void } | undefined;
}) {
  const detail = useQuery(tenantDomainQuery(tenantId, listed.id));
  const [copyNotice, setCopyNotice] = useState<ReactNode>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (takeFocus) ref.current?.focus();
  }, [takeFocus]);

  // The list saw a newer check than this card (the list is followed for other addresses too).
  const { data, isFetching, refetch } = detail;
  const behind = data !== undefined && detailBehind(listed, data.domain);
  useEffect(() => {
    if (behind && !isFetching) void refetch();
  }, [behind, isFetching, refetch]);

  const domain = data?.domain ?? listed;
  const dns = data?.dns ?? null;
  const observed = data?.observed ?? null;
  const twin = missingTwin(domain.hostname, domains);
  const zone = dnsZone(domain.hostname);
  const root = isRootAddress(domain.hostname);

  const copy = (value: string) => {
    // The clipboard may be unavailable or refused (permissions, plain HTTP): then say so; the
    // value stays selectable in the table.
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(value))
      .then(
        () => {
          setCopyNotice(<Filled text={he.storeDomains.copied} values={{ value }} />);
        },
        () => {
          setCopyNotice(he.storeDomains.copyFailed);
        },
      );
  };

  return (
    <div
      ref={ref}
      tabIndex={-1}
      data-testid="domain-card"
      data-hostname={domain.hostname}
      className="rounded-lg focus:outline-2 focus:outline-brand-600"
    >
      <Card
        title={fill(needsSetup(domain) ? he.storeDomains.setupTitle : he.storeDomains.detailTitle, {
          hostname: domain.hostname,
        })}
      >
        <div className="flex flex-col gap-5">
          <div data-testid="domain-status-text" data-status={domain.status}>
            <Alert tone={statusTones[domain.status]}>
              <div className="flex flex-col gap-2">
                <p className="font-medium">{statusText(domain, certificateIssuing)}</p>
                {domain.reason !== null && showsReason(domain) ? (
                  <p data-testid="domain-reason" data-reason={domain.reason}>
                    {/* A root address has no CNAME to name as the target. */}
                    {reasonText(domain.reason, root ? null : dns)}
                  </p>
                ) : null}
                {domain.status === 'PENDING' && domain.verifyDeadlineAt !== null ? (
                  <p>
                    {fill(he.storeDomains.deadline, {
                      date: formatDateTime(domain.verifyDeadlineAt),
                    })}
                  </p>
                ) : null}
                {domain.status === 'ACTIVE' && domain.tlsValidUntil !== null ? (
                  <p>
                    {fill(he.storeDomains.tlsValidUntil, {
                      date: formatDate(domain.tlsValidUntil),
                    })}
                  </p>
                ) : null}
                {issue === undefined && !issuing ? null : (
                  <div>
                    <Button
                      data-testid="domain-issue-card"
                      busy={issuing}
                      disabled={issue === undefined || issue.disabled}
                      onClick={issue?.run}
                    >
                      {he.storeDomains.issueCertificate}
                    </Button>
                  </div>
                )}
                {domain.status === 'FAILED' ? (
                  <>
                    <p>{he.storeDomains.failedHelp}</p>
                    {retry === undefined ? null : (
                      <div>
                        <Button
                          tone="secondary"
                          data-testid="domain-retry"
                          busy={retry.busy}
                          disabled={retry.disabled}
                          onClick={retry.run}
                        >
                          {he.common.retry}
                        </Button>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </Alert>
          </div>

          {issuing ? (
            <div role="status" data-testid="domain-issue-waiting">
              <Alert tone="info">{he.storeDomains.issueWaiting}</Alert>
            </div>
          ) : issued === undefined ||
            // Once the check made the address active, its own state says all there is to say.
            (issued.result === 'ISSUED' && !canIssueCertificate(domain)) ? null : (
            <IssueOutcome outcome={issued} />
          )}

          {detail.isPending ? <Loading /> : null}
          {detail.isError ? (
            <LoadError
              error={detail.error}
              busy={detail.isFetching}
              onRetry={() => {
                void detail.refetch();
              }}
            />
          ) : null}

          {dns !== null && needsSetup(domain) ? (
            <>
              <p className="text-sm text-slate-700">
                {root ? he.storeDomains.setupIntroRoot : he.storeDomains.setupIntro}
              </p>
              <NameGuide zone={zone} hostname={domain.hostname} />
              {setupKinds(domain.hostname).map((kind) => {
                const option = setupOption(dns, kind);
                if (option === undefined) return null;
                return (
                  <SetupOption
                    key={kind}
                    option={option}
                    title={root ? he.storeDomains.rootOptionTitle : he.storeDomains.options[kind]}
                    zone={zone}
                    onCopy={copy}
                  >
                    {kind === 'A_TXT' && !dns.addressesKnown ? (
                      <Alert tone="warning">
                        <div
                          className="flex flex-wrap items-center justify-between gap-3"
                          data-testid="dns-addresses-unknown"
                        >
                          <span>{he.storeDomains.addressesUnknown}</span>
                          <Button
                            tone="secondary"
                            busy={detail.isFetching}
                            onClick={() => {
                              void detail.refetch();
                            }}
                          >
                            {he.storeDomains.refresh}
                          </Button>
                        </div>
                      </Alert>
                    ) : null}
                  </SetupOption>
                );
              })}
              <p
                role="status"
                aria-live="polite"
                data-testid="dns-copy-status"
                className="min-h-5 text-xs text-slate-600"
              >
                {copyNotice}
              </p>
              {/* A root address shows its own records only, with no word about the www one. */}
              {twin === undefined || root ? null : (
                <p className="text-sm text-slate-700" data-testid="domain-twin-hint">
                  {fill(he.storeDomains.twinHint, { hostname: twin })}
                </p>
              )}
            </>
          ) : null}

          {observed === null ? null : <Observed observed={observed} />}
        </div>
      </Card>
    </div>
  );
}

const issueTones = {
  ISSUED: 'success',
  FAILED: 'error',
  TIMEOUT: 'warning',
} as const satisfies Record<IssueCertificateResponse['result'], 'error' | 'success' | 'warning'>;

/** How asking for the certificate ended: in words, and with what the server printed on a failure. */
function IssueOutcome({ outcome }: { outcome: IssueCertificateResponse }) {
  return (
    <div
      role="status"
      data-testid="domain-issue-result"
      data-result={outcome.result}
      data-failure={outcome.failure ?? ''}
    >
      <Alert tone={issueTones[outcome.result]}>
        <div className="flex flex-col gap-2">
          <p className="font-medium">{he.storeDomains.issueResults[outcome.result]}</p>
          {outcome.failure === null ? null : (
            <p>{he.storeDomains.issueFailures[outcome.failure]}</p>
          )}
          {outcome.output === null ? null : (
            <details>
              <summary className="cursor-pointer text-xs">{he.storeDomains.issueOutput}</summary>
              <pre
                dir="ltr"
                data-testid="domain-issue-output"
                className="ltr mt-2 max-h-48 overflow-auto text-xs whitespace-pre-wrap"
              >
                {outcome.output}
              </pre>
            </details>
          )}
        </div>
      </Alert>
    </div>
  );
}

/**
 * What goes in the "Name" field: the most common mistake is the full address, which the
 * provider then doubles (`www.shop.co.il.shop.co.il`).
 */
function NameGuide({ zone, hostname }: { zone: string; hostname: string }) {
  const values = { zone, doubled: `${hostname}.${zone}` };
  return (
    <div data-testid="dns-name-guide">
      <Alert tone="info">
        <div className="flex flex-col gap-2">
          <p className="font-medium">{he.storeDomains.nameGuideTitle}</p>
          <ul className="list-disc ps-5">
            {he.storeDomains.nameGuide.map((line) => (
              <li key={line}>
                <Filled text={line} values={values} />
              </li>
            ))}
          </ul>
        </div>
      </Alert>
    </div>
  );
}

/** A sentence with addresses in it: each address is set left to right inside the Hebrew. */
function Filled({ text, values }: { text: string; values: Record<string, string> }) {
  return text.split(/(\{\w+\})/).map((part, index) => {
    const value = /^\{(\w+)\}$/.exec(part)?.[1];
    return value !== undefined && values[value] !== undefined ? (
      <Ltr key={index}>{values[value]}</Ltr>
    ) : (
      part
    );
  });
}

/** One way to connect the address: what it is for, and its records to copy. */
function SetupOption({
  option,
  title,
  zone,
  onCopy,
  children,
}: {
  option: DnsSetupOption;
  title: string;
  zone: string;
  onCopy: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-slate-200 p-4"
      data-testid={`dns-option-${option.kind}`}
    >
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600">{he.storeDomains.optionText[option.kind]}</p>
      {children}
      {option.records.length === 0 ? null : (
        <Table
          head={[
            he.storeDomains.recordType,
            he.storeDomains.recordName,
            he.storeDomains.recordValue,
          ]}
        >
          {option.records.map((record) => {
            const host = recordHost(record.name, zone);
            return (
              <tr
                key={`${record.type}|${record.name}|${record.value}`}
                data-testid="dns-record"
                data-type={record.type}
              >
                <Cell className="whitespace-nowrap font-mono text-xs">
                  {he.storeDomains.recordTypes[record.type]}
                </Cell>
                <Cell>
                  <div className="flex items-center gap-2">
                    <code
                      dir="ltr"
                      data-testid="dns-record-host"
                      className="ltr rounded bg-slate-100 px-1.5 py-0.5 font-mono text-sm font-semibold whitespace-nowrap text-slate-900"
                    >
                      {host}
                    </code>
                    <Button
                      tone="secondary"
                      data-testid="dns-record-copy-name"
                      aria-label={fill(he.storeDomains.copyName, {
                        type: he.storeDomains.recordTypes[record.type],
                        value: host,
                      })}
                      onClick={() => {
                        onCopy(host);
                      }}
                    >
                      {he.storeDomains.copy}
                    </Button>
                  </div>
                  <p data-testid="dns-record-name" className="mt-1 text-xs text-slate-500">
                    <Filled text={he.storeDomains.fullName} values={{ name: record.name }} />
                  </p>
                </Cell>
                <Cell>
                  <div className="flex items-center gap-2">
                    <code
                      dir="ltr"
                      data-testid="dns-record-value"
                      data-type={record.type}
                      className="ltr font-mono text-xs whitespace-nowrap"
                    >
                      {record.value}
                    </code>
                    <Button
                      tone="secondary"
                      data-testid="dns-record-copy"
                      aria-label={fill(he.storeDomains.copyValue, {
                        type: he.storeDomains.recordTypes[record.type],
                        value: record.value,
                      })}
                      onClick={() => {
                        onCopy(record.value);
                      }}
                    >
                      {he.storeDomains.copy}
                    </Button>
                  </div>
                </Cell>
              </tr>
            );
          })}
        </Table>
      )}
    </section>
  );
}

/** What the last check found in DNS, so a typo is easy to spot. */
function Observed({ observed }: { observed: DomainObservation }) {
  const rows: readonly (readonly [string, ReactNode])[] = [
    [
      he.storeDomains.observedCname,
      observed.cname.length === 0 ? (
        he.storeDomains.observedNoCname
      ) : (
        <Ltr>{observed.cname.join(' → ')}</Ltr>
      ),
    ],
    [
      he.storeDomains.observedAddresses,
      observed.addresses.length === 0 ? (
        he.storeDomains.observedNoAddresses
      ) : (
        <Ltr>{observed.addresses.join(', ')}</Ltr>
      ),
    ],
    [
      he.storeDomains.observedTxt,
      observed.txtMatches ? he.storeDomains.observedTxtYes : he.storeDomains.observedTxtNo,
    ],
    ...(observed.caaBlocked
      ? ([[he.storeDomains.observedCaa, he.storeDomains.observedCaaBlocked]] as const)
      : []),
  ];
  return (
    <section
      className="flex flex-col gap-2"
      data-testid="domain-observed"
      data-txt={String(observed.txtMatches)}
      data-caa-blocked={String(observed.caaBlocked)}
    >
      <h3 className="text-sm font-semibold text-slate-900">{he.storeDomains.observedTitle}</h3>
      <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-slate-500">{label}</dt>
            <dd className="min-w-0 break-words text-slate-800">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Ltr({ children }: { children: ReactNode }) {
  return (
    <code dir="ltr" className="ltr font-mono text-xs">
      {children}
    </code>
  );
}
